import { z } from 'zod';

import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { stripUndefined } from '@/shared/lib/stripUndefined';
import { NotFoundError, ValidationError } from '@/shared/lib/errors';
import { resendInvitation } from '@/features/invitations/api';
import {
  groupSchema,
  groupCreateSchema,
  groupUpdateSchema,
  type Group,
  type GroupCreate,
  type GroupUpdate,
} from '@shared/schemas/groups';

// ==================== Query keys ====================

export const groupsKeys = {
  all: ['groups'] as const,
  list: () => [...groupsKeys.all, 'list'] as const,
  byId: (id: string) => [...groupsKeys.all, 'id', id] as const,
  members: (id: string) => [...groupsKeys.all, 'id', id, 'members'] as const,
};

// ==================== Queries ====================

/** Grupos activos donde el usuario es miembro (RLS lo filtra). */
export async function fetchMyGroups(): Promise<Group[]> {
  // Usamos la RPC my_groups() en vez de SELECT * FROM groups porque la RLS de
  // groups permite a los super_admin ver TODOS los grupos del sistema (para
  // gestionar desde /admin). En "Mis grupos" queremos solo aquellos donde el
  // caller es realmente miembro activo, sin importar si es super_admin.
  const { data, error } = await supabase.rpc('my_groups');

  if (error) {
    logger.error('fetchMyGroups failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return (data ?? []).map((row: unknown) => groupSchema.parse(row));
}

export async function fetchGroup(groupId: string): Promise<Group> {
  const { data, error } = await supabase
    .from('groups')
    .select('*')
    .eq('id', groupId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) {
    logger.error('fetchGroup failed', { code: error.code, groupId });
    throw new Error(error.message);
  }
  if (!data) throw new NotFoundError('Group', groupId);
  return groupSchema.parse(data);
}

/**
 * La fila tal y como llega, **con `auth_user_id` y sin `is_ghost`**.
 *
 * `is_ghost` no es una columna: se calcula aquí como `auth_user_id === null`. Y
 * el `select()` de abajo es una cadena de texto que TypeScript no lee, así que
 * quitarle esa columna compilaba, pasaba los tests y dejaba a **todos los
 * fantasmas pareciendo cuentas registradas** —sin distintivo de «pendiente» y
 * sin poder darles correo—, en silencio. Por eso la fila se parsea en vez de
 * afirmarse con un `as unknown as`: si la columna falta, revienta aquí.
 */
const groupMemberRowSchema = z.object({
  id: z.string(),
  group_id: z.string(),
  participant_id: z.string(),
  role: z.enum(['admin', 'member']),
  joined_at: z.string(),
  left_at: z.string().nullable(),
  participant: z.object({
    id: z.string(),
    display_name: z.string(),
    email: z.string().nullable(),
    avatar_url: z.string().nullable(),
    auth_user_id: z.string().nullable(),
  }),
});

export interface GroupMemberWithParticipant {
  id: string;
  group_id: string;
  participant_id: string;
  role: 'admin' | 'member';
  joined_at: string;
  left_at: string | null;
  participant: {
    id: string;
    display_name: string;
    email: string | null;
    avatar_url: string | null;
    is_ghost: boolean;
  };
}

export async function fetchGroupMembers(groupId: string): Promise<GroupMemberWithParticipant[]> {
  const { data, error } = await supabase
    .from('group_members')
    .select(
      `
      id, group_id, participant_id, role, joined_at, left_at,
      participant:participants!inner (
        id, display_name, email, avatar_url, auth_user_id
      )
    `
    )
    .eq('group_id', groupId)
    .is('left_at', null);

  if (error) {
    logger.error('fetchGroupMembers failed', { code: error.code, groupId });
    throw new Error(error.message);
  }

  return z
    .array(groupMemberRowSchema)
    .parse(data ?? [])
    .map(({ participant, ...row }) => ({
      ...row,
      participant: {
        id: participant.id,
        display_name: participant.display_name,
        email: participant.email,
        avatar_url: participant.avatar_url,
        is_ghost: participant.auth_user_id === null,
      },
    }));
}

// ==================== Mutations ====================

export async function createGroup(input: GroupCreate): Promise<Group> {
  const parsed = groupCreateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError('Datos de grupo inválidos', {
      issues: parsed.error.flatten().fieldErrors,
    });
  }

  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('Not authenticated');

  // Necesitamos el participant.id para created_by
  const { data: me, error: meErr } = await supabase
    .from('participants')
    .select('id')
    .eq('auth_user_id', userData.user.id)
    .is('deleted_at', null)
    .maybeSingle();
  if (meErr || !me) throw new Error('Participant profile not found');

  const { data, error } = await supabase
    .from('groups')
    .insert(stripUndefined({
      ...parsed.data,
      created_by: me.id,
    }))
    .select('*')
    .single();

  if (error) {
    logger.error('createGroup failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return groupSchema.parse(data);
}

export async function updateGroup(groupId: string, input: GroupUpdate): Promise<Group> {
  const parsed = groupUpdateSchema.safeParse(input);
  if (!parsed.success) {
    throw new ValidationError('Datos de grupo inválidos');
  }
  const { data, error } = await supabase
    .from('groups')
    .update(stripUndefined(parsed.data))
    .eq('id', groupId)
    .select('*')
    .single();
  if (error) {
    logger.error('updateGroup failed', { code: error.code });
    throw new Error(error.message);
  }
  return groupSchema.parse(data);
}

/**
 * Reconvierte los gastos del grupo a su divisa actual usando el tipo de cambio
 * del día de cada gasto. Se llama al cambiar la divisa del grupo: hasta ahora
 * los gastos existentes conservaban su conversión antigua (un gasto de 39 €
 * pasaba a contar como 39 ¥) pese a que la pantalla de edición prometía lo
 * contrario.
 *
 * Devuelve cuántos gastos se actualizaron y cuántos se quedaron sin tocar por
 * no haber cotización para su fecha.
 */
export async function recomputeConversions(
  groupId: string
): Promise<{ currency: string; updated: number; without_rate: number }> {
  const { data, error } = await supabase.rpc('group_recompute_conversions', {
    p_group_id: groupId,
  });
  if (error) {
    logger.error('recomputeConversions failed', { code: error.code });
    throw new Error(error.message);
  }
  return data as unknown as { currency: string; updated: number; without_rate: number };
}

export interface ConversionGaps {
  target_currency: string;
  total: number;
  oldest: string | null;
  newest: string | null;
  by_currency: { currency: string; expenses: number; oldest: string; newest: string }[];
}

/**
 * Cuántos gastos del grupo NO se podrán reconvertir a `targetCurrency`, por no
 * haber cotización para su fecha.
 *
 * Sólo lectura. Sirve para avisar ANTES de cambiar la divisa: la reconversión
 * la hace un trigger dentro del UPDATE y deja esos gastos con la conversión
 * vieja en silencio, sin que nada lo diga (#15).
 *
 * `targetCurrency` es la divisa CANDIDATA, no la actual. Con `null` responde
 * sobre la que el grupo ya tiene.
 */
export async function groupConversionGaps(
  groupId: string,
  targetCurrency: string | null
): Promise<ConversionGaps> {
  const { data, error } = await supabase.rpc('group_conversion_gaps', {
    p_group_id: groupId,
    // **La clave se omite**, no se manda nula. En SQL el parámetro es
    // `p_target_currency text DEFAULT NULL`, así que omitirlo y mandarlo nulo
    // dan lo mismo; y los tipos generados expresan «tiene valor por omisión»
    // como opcional, que con `exactOptionalPropertyTypes` no admite ni siquiera
    // un `undefined` explícito. Lo destapó regenerar `database.types.ts`, que
    // llevaba desactualizado desde antes de `device_push_tokens`.
    ...(targetCurrency !== null ? { p_target_currency: targetCurrency } : {}),
  });
  if (error) {
    logger.error('groupConversionGaps failed', { code: error.code });
    throw new Error(error.message);
  }
  return data as unknown as ConversionGaps;
}

export async function archiveGroup(groupId: string): Promise<Group> {
  return updateGroup(groupId, { is_archived: true });
}

export async function unarchiveGroup(groupId: string): Promise<Group> {
  return updateGroup(groupId, { is_archived: false });
}

export interface RecoverableGroup {
  id: string;
  name: string;
  emoji: string | null;
  deleted_at: string;
  days_left: number;
}

export async function fetchRecoverableGroups(): Promise<RecoverableGroup[]> {
  const { data, error } = await supabase.rpc('my_recoverable_groups');
  if (error) {
    logger.error('fetchRecoverableGroups failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
  return (data ?? []) as RecoverableGroup[];
}

export async function restoreGroup(groupId: string): Promise<void> {
  const { error } = await supabase.rpc('group_restore', { p_group_id: groupId });
  if (error) {
    logger.error('restoreGroup failed', { code: error.code, message: error.message });
    throw new Error(error.message);
  }
}

export async function softDeleteGroup(groupId: string): Promise<void> {
  const { error } = await supabase.rpc('group_soft_delete', { p_group_id: groupId });
  if (error) {
    logger.error('softDeleteGroup failed', { code: error.code, message: error.message });
    throw mapMemberActionError(error);
  }
}

/** El usuario actual sale del grupo. Bloquea si tiene deudas o es único admin. */
export async function leaveGroup(groupId: string): Promise<void> {
  const { error } = await supabase.rpc('member_leave_group', { p_group_id: groupId });
  if (error) {
    logger.error('leaveGroup failed', { code: error.code, message: error.message });
    throw mapMemberActionError(error);
  }
}

/** Promover un miembro a admin. Solo admins. */
export async function promoteMember(groupId: string, participantId: string): Promise<void> {
  const { error } = await supabase.rpc('member_promote', {
    p_group_id: groupId,
    p_participant_id: participantId,
  });
  if (error) {
    logger.error('promoteMember failed', { code: error.code, message: error.message });
    throw mapMemberActionError(error);
  }
}

/** Degradar un admin a member. Debe quedar al menos un admin. */
export async function demoteMember(groupId: string, participantId: string): Promise<void> {
  const { error } = await supabase.rpc('member_demote', {
    p_group_id: groupId,
    p_participant_id: participantId,
  });
  if (error) {
    logger.error('demoteMember failed', { code: error.code, message: error.message });
    throw mapMemberActionError(error);
  }
}

/** Expulsar un miembro. `force` permite hacerlo aunque tenga balance ≠ 0. */
export async function removeMember(
  groupId: string,
  participantId: string,
  force = false
): Promise<{ balance_cents_at_removal: number }> {
  const { data, error } = await supabase.rpc('member_remove', {
    p_group_id: groupId,
    p_participant_id: participantId,
    p_force: force,
  });
  if (error) {
    logger.error('removeMember failed', { code: error.code, message: error.message });
    throw mapMemberActionError(error);
  }
  return data as { balance_cents_at_removal: number };
}

/**
 * Traduce errores de las RPCs a códigos estables para que el frontend
 * los reconozca sin inspeccionar el mensaje.
 *
 * P0002 con DETAIL JSON → balance/deudas pendientes: adjuntamos el detalle.
 * P0001 → violación de invariante lógica (único admin, ya es admin, etc).
 * 42501 → no autorizado.
 */
function mapMemberActionError(err: {
  code?: string | undefined;
  message?: string | undefined;
  details?: string | undefined;
}): Error {
  const e = new Error(err.message ?? 'Unknown error') as Error & {
    kind: string;
    detail?: unknown;
  };
  if (err.code === 'P0002') {
    e.kind = 'pending_balance';
    try {
      if (err.details) e.detail = JSON.parse(err.details);
    } catch {
      /* ignore */
    }
  } else if (err.code === 'P0001') {
    e.kind = 'invariant';
  } else if (err.code === '42501') {
    e.kind = 'forbidden';
  } else {
    e.kind = 'unknown';
  }
  return e;
}

// ==================== Darle correo a un fantasma ====================

/**
 * Lo que devuelve dar de alta el correo de un fantasma.
 *
 * `email_sent` es **best-effort**: el correo ya está asignado y la invitación
 * abierta aunque el envío falle, así que quien lo pinte tiene que distinguir
 * «hecho y avisado» de «hecho, pero el aviso no salió» — decir «no se pudo»
 * sería mentira y llevaría a reintentar algo que ya está hecho.
 */
export interface GhostEmailResult {
  participant_id: string;
  email: string;
  invitation_id: string;
  email_sent: boolean;
}

/**
 * Le da correo a un participante fantasma del grupo y le manda la invitación.
 *
 * Son **dos pasos** y en este orden: la RPC escribe el correo en la fila que ya
 * existe —con su historial de gastos— y abre la invitación; después
 * `resend-invitation` la envía. Si el segundo falla, el primero ya ocurrió y no
 * se deshace: es correcto, porque la persona ya tiene correo y la invitación
 * sigue ahí para reenviarla.
 *
 * El correo no puede ser de nadie más: el servidor responde `23505` si ya es de
 * otro participante o de una cuenta.
 */
export async function setGhostEmail(
  groupId: string,
  participantId: string,
  email: string
): Promise<GhostEmailResult> {
  const { data, error } = await supabase.rpc('group_admin_set_ghost_email', {
    p_participant_id: participantId,
    p_group_id: groupId,
    p_email: email,
  });
  if (error) {
    logger.error('setGhostEmail failed', { code: error.code, message: error.message });
    throw mapGhostEmailError(error);
  }

  // Sin error y sin fila no puede pasar —la función siempre devuelve el jsonb o
  // lanza—, pero si pasara, seguir adelante reventaría con un TypeError dentro
  // del `catch` de abajo y el fallo escaparía sin `kind`, que es la única cosa
  // que la pantalla sabe traducir.
  const asignado = data as { participant_id: string; email: string; invitation_id: string } | null;
  if (!asignado?.invitation_id) {
    logger.error('setGhostEmail: respuesta sin invitación', { participantId });
    throw mapGhostEmailError({ code: 'EMPTY', message: 'Respuesta vacía del servidor' });
  }

  // El envío va aparte y **no** tumba la operación: reutiliza `resend-invitation`,
  // que ya sabe hablar con Brevo y escapa el HTML con su helper `esc`. Plantilla
  // no hay ninguna: el HTML lo monta la propia función.
  //
  // Las dos verrugas que tuvo esto —ni límite de reenvíos, y un correo que decía
  // «Recordatorio» a quien lo recibía por primera vez— **están arregladas** desde
  // la v10 (incidencia #26), y este camino es justo el que menos las nota: la RPC
  // acaba de crear la invitación con `last_sent_at` a `NULL`, así que el envío no
  // choca contra el límite de 60 minutos y el texto sale como primer aviso.
  //
  // Por eso `first_time` de la respuesta es siempre `true` aquí, y no se lee:
  // sería una rama que no puede ejecutarse.
  let emailSent = false;
  try {
    const res = await resendInvitation(asignado.invitation_id);
    emailSent = res.email_sent;
  } catch (err) {
    logger.error('setGhostEmail: invitación asignada pero no enviada', {
      invitationId: asignado.invitation_id,
      message: err instanceof Error ? err.message : String(err),
    });
  }

  return { ...asignado, email_sent: emailSent };
}

/**
 * Traduce los `SQLSTATE` de `group_admin_set_ghost_email` a algo que la pantalla
 * pueda contar. Son los mismos códigos de siempre, pero aquí `23505` significa
 * una cosa muy concreta —ese correo ya es de alguien— que merece su propio texto
 * en vez de un «error» genérico.
 */
function mapGhostEmailError(err: { code?: string | undefined; message?: string | undefined }): Error {
  const e = new Error(err.message ?? 'Unknown error') as Error & { kind: string };
  if (err.code === '23505') e.kind = 'email_taken';
  else if (err.code === '42501') e.kind = 'forbidden';
  else if (err.code === '22023') e.kind = 'invalid';
  else if (err.code === 'P0002') e.kind = 'not_member';
  else e.kind = 'unknown';
  return e;
}
